<template>
  <Teleport to="body">
    <transition name="search-modal">
      <div v-if="isSearchModal" class="search-overlay" @click.self="closeModal">
        <div class="search-panel">
          <!-- 搜索框 -->
          <div class="search-input-wrap">
            <Icon icon="lucide:search" :size="22" class="search-icon" />
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              placeholder="搜索产品、解决方案、文档..."
              class="search-input"
              @keydown.enter="handleSearch"
            />
            <button class="search-close-btn" @click="closeModal">
              <Icon icon="lucide:x" :size="20" />
            </button>
          </div>

          <!-- 搜索结果 / 热门搜索 -->
          <div class="search-content">
            <template v-if="!searchQuery">
              <!-- 热门搜索 -->
              <div class="search-section">
                <h4 class="search-section-title">热门搜索</h4>
                <div class="hot-tags">
                  <span
                    v-for="tag in hotTags"
                    :key="tag"
                    class="hot-tag"
                    @click="searchQuery = tag; handleSearch()"
                  >{{ tag }}</span>
                </div>
              </div>

              <!-- 快捷入口 -->
              <div class="search-section">
                <h4 class="search-section-title">快捷入口</h4>
                <div class="quick-links">
                  <div
                    v-for="link in quickLinks"
                    :key="link.label"
                    class="quick-link-item"
                    @click="navigateTo(link.path)"
                  >
                    <div class="quick-link-icon" :style="{ background: link.bg, color: link.color }">
                      <Icon :icon="link.icon" :size="20" />
                    </div>
                    <span>{{ link.label }}</span>
                  </div>
                </div>
              </div>
            </template>

            <!-- 搜索结果 -->
            <template v-else>
              <div class="search-results">
                <div v-if="filteredProducts.length" class="result-group">
                  <h4 class="result-group-title">产品 ({{ filteredProducts.length }})</h4>
                  <div
                    v-for="p in filteredProducts"
                    :key="p.id"
                    class="result-item"
                    @click="navigateTo(`/portal/products/${p.id}`)"
                  >
                    <div class="result-icon" :style="{ background: p.bgColor, color: p.color }">
                      <Icon :icon="p.icon" :size="18" />
                    </div>
                    <div>
                      <p class="result-name">{{ p.name }}</p>
                      <p class="result-desc">{{ p.descShort }}</p>
                    </div>
                  </div>
                </div>

                <div v-if="filteredSolutions.length" class="result-group">
                  <h4 class="result-group-title">解决方案 ({{ filteredSolutions.length }})</h4>
                  <div
                    v-for="s in filteredSolutions"
                    :key="s.id"
                    class="result-item"
                    @click="navigateTo(`/portal/solutions/${s.id}`)"
                  >
                    <div class="result-icon" :style="{ background: s.color + '20', color: s.color }">
                      <Icon :icon="s.icon" :size="18" />
                    </div>
                    <div>
                      <p class="result-name">{{ s.name }}</p>
                      <p class="result-desc">{{ s.desc }}</p>
                    </div>
                  </div>
                </div>

                <div v-if="!filteredProducts.length && !filteredSolutions.length" class="no-result">
                  <Icon icon="lucide:search-x" :size="40" />
                  <p>未找到 "{{ searchQuery }}" 相关结果</p>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Icon } from '@/components/Icon'
import { usePortalState } from './usePortalState'
import { products, solutions } from './data/portalData'

defineOptions({ name: 'SearchModal' })

const router = useRouter()
const { isSearchModal, closeModal } = usePortalState()

const searchQuery = ref('')
const searchInputRef = ref<HTMLInputElement>()

const hotTags = ['官网建设', '商城系统', 'SEO优化', '会员系统', '零售电商', '智能制造']

const quickLinks = [
  { label: '产品中心', icon: 'lucide:layout-grid', color: '#0066FF', bg: '#EBF5FF', path: '/portal/products' },
  { label: '解决方案', icon: 'lucide:lightbulb', color: '#7C3AED', bg: '#F3E8FF', path: '/portal/solutions' },
  { label: '帮助中心', icon: 'lucide:help-circle', color: '#00B96B', bg: '#ECFDF5', path: '/portal/help-center' },
  { label: '免费试用', icon: 'lucide:gift', color: '#FF9500', bg: '#FFFBEB', path: '/portal/free-trial' },
  { label: '关于我们', icon: 'lucide:building', color: '#0891B2', bg: '#ECFEFF', path: '/portal/about' },
  { label: '联系我们', icon: 'lucide:phone', color: '#E11D48', bg: '#FFF1F2', path: '/portal/contact' }
]

const filteredProducts = computed(() => {
  if (!searchQuery.value) return []
  const q = searchQuery.value.toLowerCase()
  return products.filter(p =>
    p.name.toLowerCase().includes(q) ||
    p.desc.toLowerCase().includes(q) ||
    p.descShort.toLowerCase().includes(q)
  )
})

const filteredSolutions = computed(() => {
  if (!searchQuery.value) return []
  const q = searchQuery.value.toLowerCase()
  return solutions.filter(s =>
    s.name.toLowerCase().includes(q) ||
    s.desc.toLowerCase().includes(q)
  )
})

watch(isSearchModal, (val) => {
  if (val) {
    searchQuery.value = ''
    nextTick(() => {
      searchInputRef.value?.focus()
    })
  }
})

function handleSearch() {
  // 搜索逻辑已在 computed 中处理
}

function navigateTo(path: string) {
  closeModal()
  router.push(path)
}
</script>

<style lang="scss" scoped>
.search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;
}

.search-panel {
  width: 90%;
  max-width: 640px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.search-input-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #f3f4f6;
}

.search-icon {
  color: #9ca3af;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  height: 32px;
  border: none;
  outline: none;
  font-size: 17px;
  font-weight: 500;
  color: #111827;

  &::placeholder {
    color: #d1d5db;
  }
}

.search-close-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: #f3f4f6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: background 0.2s;

  &:hover {
    background: #e5e7eb;
  }
}

.search-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 16px 24px 24px;
}

.search-section {
  margin-bottom: 24px;
}

.search-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 12px;
}

.hot-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.hot-tag {
  padding: 8px 16px;
  background: #f9fafb;
  border-radius: 20px;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #ebf5ff;
    color: #0066ff;
  }
}

.quick-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.quick-link-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
  }
}

.quick-link-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.search-results {
  .result-group {
    margin-bottom: 20px;
  }

  .result-group-title {
    font-size: 13px;
    font-weight: 600;
    color: #9ca3af;
    margin-bottom: 10px;
  }
}

.result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }
}

.result-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.result-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 2px;
}

.result-desc {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

.no-result {
  text-align: center;
  padding: 48px 0;
  color: #d1d5db;

  p {
    margin-top: 12px;
    font-size: 14px;
  }
}

// 过渡动画
.search-modal-enter-active {
  transition: opacity 0.2s ease;

  .search-panel {
    transition: transform 0.25s ease, opacity 0.25s ease;
  }
}

.search-modal-leave-active {
  transition: opacity 0.15s ease;

  .search-panel {
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
}

.search-modal-enter-from {
  opacity: 0;

  .search-panel {
    transform: translateY(-20px);
    opacity: 0;
  }
}

.search-modal-leave-to {
  opacity: 0;

  .search-panel {
    transform: translateY(-10px);
    opacity: 0;
  }
}
</style>
